// modals/BrowserModal.js - 복사 버튼 및 실제 브라우저 열기 기능 추가
import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { getInAppBrowserType } from '../utils/deviceUtils';

const BrowserModal = ({ onClose }) => {
    const [copied, setCopied] = useState(false);
    const browserType = getInAppBrowserType();

    // 브라우저 이름 매핑
    const browserNames = {
        kakao: '카카오톡',
        instagram: '인스타그램',
        facebook: '페이스북',
        naver: '네이버'
    };

    const currentBrowserName = browserNames[browserType] || '인앱 브라우저';

    // 기기별 권장 브라우저 감지
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const recommendedBrowser = isIOS ? 'Safari' : 'Chrome';
    const url = 'www.fcfunnel.com';
    const fullUrl = `https://${url}`;

    // 주소 복사 기능
    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // 클립보드 API 실패 시 폴백
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // 안드로이드 카카오 인앱 → 크롬으로 열기 (Intent)
    const handleOpenInChrome = () => {
        try {
            // 크롬이 설치되어 있는지 먼저 확인
            const intentUrl = `intent://${url}/#Intent;scheme=https;package=com.android.chrome;end`;

            // 인텐트 시도
            window.location.href = intentUrl;

            // 크롬이 없으면 바로 웹 URL로 이동 (플레이스토어 거치지 않음)
            setTimeout(() => {
                window.location.href = fullUrl;
            }, 1000);
        } catch (_) {
            // 최종 폴백: 웹 URL 열기
            window.location.href = fullUrl;
        }
    };



    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                <div className="text-center mb-6">
                    <div className="text-4xl mb-3">⚠️</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">브라우저 호환성 안내</h3>
                </div>

                <div className="text-gray-700 leading-relaxed mb-6">
                    {browserType === 'kakao' ? (
                        <>
                            <p className="mb-4 text-center font-medium">
                                FC퍼널은 <strong className="text-blue-600">Chrome(갤럭시), Safari(아이폰) 브라우저</strong>에서 최적화되어 작동합니다.
                            </p>
                            <p className="mb-6 text-center">
                                카카오톡 인앱 브라우저에서는 일부 기능이 제한될 수 있습니다.<br />
                                더 나은 경험을 위해 아래 주소를 <strong className="text-blue-600">{recommendedBrowser}</strong>에서 열어주세요.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="mb-4 text-center font-medium">
                                FC퍼널은 <strong className="text-blue-600">Chrome, Safari 브라우저</strong>에서만 정상 작동합니다.
                            </p>
                            <p className="mb-6 text-center">
                                지금은 <strong className="text-red-600">{currentBrowserName}</strong>에 내장된 브라우저이므로
                                아래 주소를 {recommendedBrowser}에서 열어주시기 바랍니다.
                            </p>
                        </>
                    )}

                    {/* 주소창과 복사 버튼 */}
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-lg font-mono text-gray-800 flex-1 text-center">
                                {url}
                            </span>
                            <button
                                onClick={handleCopyUrl}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${copied
                                    ? 'bg-green-500 text-white'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                title="주소 복사"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        복사됨
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        복사
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                        <div className="text-sm text-green-800">
                            <strong>💡 추가 팁:</strong><br />
                            {recommendedBrowser} 브라우저에서 '홈 화면에 추가'를 하시면 더욱 편리하게 활용 가능합니다.
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    {/* 카카오톡 인앱 브라우저 전용: 외부 브라우저로 열기 */}
                    {browserType === 'kakao' && (
                        <>
                            {!isIOS ? (
                                // 안드로이드: 크롬으로 열기
                                <button
                                    onClick={handleOpenInChrome}
                                    className="w-full py-3 px-4 rounded-xl font-bold bg-green-600 text-white hover:bg-green-700 transition-colors"
                                >
                                    Chrome으로 열기
                                </button>
                            ) : (
                                // iOS: Safari로 열기
                                <button
                                    onClick={() => {
                                        // Safari로 열기 시도
                                        window.location.href = fullUrl;
                                    }}
                                    className="w-full py-3 px-4 rounded-xl font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                                >
                                    Safari로 열기
                                </button>
                            )}
                        </>
                    )}

                    <button
                        onClick={onClose}
                        className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-bold hover:bg-gray-300 transition-colors"
                    >
                        닫기
                    </button>
                </div>

                {/* 추가 안내 */}
                <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500">
                        복사된 주소를 {recommendedBrowser} 브라우저 주소창에<br />
                        직접 입력해주세요.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default BrowserModal;